import { Chess, type PieceSymbol, type Square } from "chess.js";
import type { BoardArrow, BoardHighlight } from "@/shared/stores/board.store";

const ANY_MARKER_PATTERN = /\[(?:FEN|POSITION|ARROW|HIGHLIGHT|ORIENTATION|GAME|PLY_RANGE|COMPARE|EVAL_LINE):[^\]]+\]/g;

const SQUARE_PATTERN = /^[a-h][1-8]$/;
const markerInteger = (value: string | undefined, maximum: number): number | null => {
  if (!value || value.length > 4 || [...value].some((character) => character < "0" || character > "9")) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= maximum ? parsed : null;
};
const markerGameId = (value: string | undefined): string | null => {
  if (!value) return null;
  const parts = value.split("-");
  const lengths = [8, 4, 4, 4, 12];
  const valid =
    parts.length === lengths.length &&
    parts.every((part, index) => {
      if (part.length !== lengths[index]) return false;
      return [...part].every(
        (character) =>
          (character >= "0" && character <= "9") || (character.toLowerCase() >= "a" && character.toLowerCase() <= "f"),
      );
    });
  return valid ? value : null;
};

/** Named colors we trust enough to hand straight to inline styles/SVG, plus hex/rgb forms. Anything else is dropped rather than passed through unchecked. */
const SAFE_COLOR_PATTERN = /^(?:red|orange|yellow|green|blue|purple|#[0-9a-f]{3,8}|rgba?\([\d.,\s%]+\))$/i;
const ROLE_NAMES: Record<PieceSymbol, string> = {
  p: "pawn",
  n: "knight",
  b: "bishop",
  r: "rook",
  q: "queen",
  k: "king",
};

export interface MarkerWarning {
  kind: "invalid-fen" | "illegal-arrow" | "invalid-square" | "orphan-marker" | "invalid-orientation";
  /** The raw marker content that was rejected, for debugging/support — never shown verbatim to the player, see the correction UI copy instead. */
  raw: string;
}

export interface BoardSegment {
  type: "board";
  /** FEN string — either from [FEN:] directly, or a placeholder when using [POSITION:]. The frontend resolves the real FEN from the API when positionRef is set. */
  fen: string;
  /** When set, the frontend should resolve the real FEN from analysis data instead of trusting the fen field. */
  positionRef: { gameId: string; moveNumber: number; ply?: number | null } | null;
  orientation: "white" | "black";
  gameId: string | null;
  plyRange: { start: number; end: number } | null;
  /** Side-by-side comparison: left = played move (red), right = best move (green). Both boards show the same starting position (fen). */
  compare: { playedFrom: string; playedTo: string; bestFrom: string; bestTo: string } | null;
  /** ID into evaluatedLineStore (see live-eval.ts) — when set, InlineBoard renders a real ◀▶ navigator through a live-computed Stockfish continuation instead of requiring a gameId/DB row. */
  evalLineId: string | null;
  arrows: BoardArrow[];
  highlights: BoardHighlight[];
  title: string | null;
}

export interface TextSegment {
  type: "text";
  content: string;
}

export type MessageSegment = TextSegment | BoardSegment;

export interface ParsedBoardReply {
  /** Ordered segments — text and boards interleaved in the order they appeared in the reply. Render each segment in sequence to show boards exactly where Nikola placed them. */
  segments: MessageSegment[];
  /** All board states (for global board store / navigator, same data as board segments above). */
  boards: {
    fen: string;
    positionRef: { gameId: string; moveNumber: number; ply?: number | null } | null;
    orientation: "white" | "black";
    gameId: string | null;
    plyRange: { start: number; end: number } | null;
    compare: { playedFrom: string; playedTo: string; bestFrom: string; bestTo: string } | null;
    evalLineId: string | null;
    arrows: BoardArrow[];
    highlights: BoardHighlight[];
  }[];
  /** Markers that were dropped because they were malformed or chess-illegal. */
  warnings: MarkerWarning[];
}

/**
 * Extracts board annotation markers from Nikola's reply (see
 * packages/coach's system-prompt.ts for the exact conventions):
 *   [FEN: <fen>]                 — starts a new position block
 *   [ARROW: e2-e4]               — draws an arrow (optional trailing color)
 *   [ARROW: e2-e4 red]
 *   [HIGHLIGHT: e4]              — highlights a square (optional trailing color)
 *   [HIGHLIGHT: e4 red]
 *   [ORIENTATION: white]         — which side of the board is shown at the bottom
 *   [ORIENTATION: black]
 *   [GAME: <uuid>]               — which analyzed game this FEN belongs to
 *
 * A reply can contain multiple FEN blocks (e.g. "before this move" /
 * "after this move") — each ARROW/HIGHLIGHT/ORIENTATION belongs to
 * whichever FEN block precedes it in the text, not to whatever the LAST
 * FEN in the reply happens to be. Only the LAST block's markers are
 * returned, since the board can only show one position at a time.
 *
 * Squares/colors are syntactically validated (real board squares, safe
 * color values). Arrows are additionally checked for chess legality
 * against that block's FEN using chess.js — an arrow whose "from" square
 * doesn't have a piece that can legally reach "to" is dropped rather
 * than drawn (a bad arrow is worse than no arrow: it teaches an illegal
 * move as if it were real). Anything dropped is recorded in `warnings`
 * rather than silently discarded, so the UI can show the player a
 * visible correction instead of pretending the reply was flawless.
 */
export const parseBoardMarkers = (reply: string): ParsedBoardReply => {
  const { blocks, warnings, blockPositions } = splitIntoBlocks(reply);

  const boards = blocks.map((block) => ({
    fen: block.fen,
    positionRef: block.positionRef,
    orientation: block.orientation ?? "white",
    gameId: block.gameId,
    plyRange: block.plyRange,
    compare: block.compare,
    evalLineId: block.evalLineId,
    arrows: block.arrows,
    highlights: block.highlights,
  }));

  // Build interleaved segments: split the reply at each FEN marker group
  // position, alternating text and board segments.
  const segments: MessageSegment[] = [];
  const cleanReply = reply;
  let lastEnd = 0;

  for (let i = 0; i < blockPositions.length; i++) {
    const { start, end } = blockPositions[i];
    const board = boards[i];

    // Text before this block
    const textBefore = cleanReply
      .slice(lastEnd, start)
      .replace(ANY_MARKER_PATTERN, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    if (textBefore) {
      segments.push({ type: "text", content: textBefore });
    }

    // Extract a title from the text immediately before this board
    // (look for patterns like "vs X", "Move N:", opponent names, etc.)
    const title = extractBoardTitle(cleanReply.slice(Math.max(0, start - 200), start));

    segments.push({
      type: "board",
      ...board,
      title,
    });

    lastEnd = end;
  }

  // Remaining text after the last block
  const textAfter = cleanReply
    .slice(lastEnd)
    .replace(ANY_MARKER_PATTERN, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  if (textAfter) {
    segments.push({ type: "text", content: textAfter });
  }

  // If no blocks at all, the whole thing is one text segment
  if (segments.length === 0) {
    segments.push({ type: "text", content: reply.trim() });
  }

  return { segments, boards, warnings };
};

/** Extracts a contextual title from text preceding a board (opponent name, move number). */
const extractBoardTitle = (textBefore: string): string | null => {
  // Look for "vs <name>" or "against <name>" or "with <name>" patterns
  const vsMatch = textBefore.match(/(?:vs\.?|against|versus|with)\s+\**([^\s*,.)]+)\**(?:[\s,.)]|$)/i);
  // Look for "Move N" or "move N:"
  const moveMatch = textBefore.match(/move\s+(\d+)/i);
  const playedAt = textBefore.toLowerCase().lastIndexOf("played");
  const playedToken =
    playedAt < 0
      ? null
      : (textBefore
          .slice(playedAt + "played".length)
          .split(/\s+/)
          .map((token) => token.replaceAll("*", "").replace(/[,.]$/, ""))
          .find((token) => /^[NBRQK]?[a-h]?[1-8]?x?[a-h][1-8][+#]?$/i.test(token)) ?? null);

  const parts: string[] = [];
  if (vsMatch) parts.push(`vs ${vsMatch[1].replace(/\*+/g, "")}`);
  if (moveMatch) parts.push(`Move ${moveMatch[1]}`);
  if (playedToken) parts.push(playedToken);

  return parts.length > 0 ? parts.join(" — ") : null;
};

interface MarkerBlock {
  fen: string;
  positionRef: { gameId: string; moveNumber: number; ply?: number | null } | null;
  orientation: "white" | "black" | null;
  gameId: string | null;
  plyRange: { start: number; end: number } | null;
  compare: { playedFrom: string; playedTo: string; bestFrom: string; bestTo: string } | null;
  evalLineId: string | null;
  arrows: BoardArrow[];
  highlights: BoardHighlight[];
}

/**
 * Splits the reply into blocks, each starting at a [FEN: ...] marker and
 * running until the next one (or end of text). Markers before the first
 * valid FEN (shouldn't normally happen given the system prompt, but
 * isn't enforced) are recorded as orphan-marker warnings, since there's
 * no position to validate them against.
 */
const splitIntoBlocks = (
  reply: string,
): { blocks: MarkerBlock[]; warnings: MarkerWarning[]; blockPositions: { start: number; end: number }[] } => {
  const markerLines = [
    ...reply.matchAll(/\[(FEN|POSITION|ARROW|HIGHLIGHT|ORIENTATION|GAME|PLY_RANGE|COMPARE|EVAL_LINE):([^\]]+)\]/g),
  ];
  const blocks: MarkerBlock[] = [];
  const blockPositions: { start: number; end: number }[] = [];
  const warnings: MarkerWarning[] = [];
  let current: MarkerBlock | null = null;

  for (const match of markerLines) {
    const [fullMatch, kind, rawValue] = match;
    const value = rawValue.trim();
    const matchStart = match.index;
    const matchEnd = matchStart + fullMatch.length;

    if (kind === "FEN") {
      if (isValidFen(value)) {
        current = {
          fen: value,
          positionRef: null,
          orientation: null,
          gameId: null,
          plyRange: null,
          compare: null,
          evalLineId: null,
          arrows: [],
          highlights: [],
        };
        blocks.push(current);
        blockPositions.push({ start: matchStart, end: matchEnd });
      } else {
        current = null;
        warnings.push({ kind: "invalid-fen", raw: value });
      }
      continue;
    }

    if (kind === "POSITION") {
      // Format: "gameId, moveNumber, ply". The final ply is optional for stored legacy replies.
      const parts = value.split(",").map((part) => part.trim());
      const gameId = markerGameId(parts[0]);
      const moveNumber = markerInteger(parts[1], 400);
      const ply = parts.length === 3 ? markerInteger(parts[2], 800) : null;
      if ((parts.length === 2 || parts.length === 3) && gameId && moveNumber && (parts.length === 2 || ply)) {
        // Use starting position as placeholder — InlineBoard resolves the real FEN from API
        current = {
          fen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
          positionRef: { gameId, moveNumber, ply },
          orientation: null,
          gameId,
          plyRange: null,
          compare: null,
          evalLineId: null,
          arrows: [],
          highlights: [],
        };
        blocks.push(current);
        blockPositions.push({ start: matchStart, end: matchEnd });
      } else {
        warnings.push({ kind: "orphan-marker", raw: value });
      }
      continue;
    }

    if (!current) {
      warnings.push({ kind: "orphan-marker", raw: value });
      continue;
    }

    // Extend current block's end position
    if (blockPositions.length > 0) {
      blockPositions[blockPositions.length - 1].end = matchEnd;
    }

    if (kind === "ARROW") {
      // Skip geometric validation for POSITION blocks (FEN is a placeholder,
      // real position resolved later by the frontend)
      if (current.positionRef) {
        const parts = value.split(/\s+/);
        const [squares, color] = parts;
        const [from, to] = (squares ?? "").split("-");
        if (from && to && isValidSquare(from) && isValidSquare(to)) {
          current.arrows.push(color && isValidColor(color) ? { from, to, color } : { from, to });
        } else {
          warnings.push({ kind: "illegal-arrow", raw: value });
        }
      } else {
        const arrow = parseArrow(value, current.fen);
        if (arrow) {
          current.arrows.push(arrow);
        } else {
          warnings.push({ kind: "illegal-arrow", raw: value });
        }
      }
    } else if (kind === "HIGHLIGHT") {
      const highlight = parseHighlight(value);
      if (highlight) {
        current.highlights.push(highlight);
      } else {
        warnings.push({ kind: "invalid-square", raw: value });
      }
    } else if (kind === "ORIENTATION") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "white" || normalized === "black") {
        current.orientation = normalized;
      } else {
        warnings.push({ kind: "invalid-orientation", raw: value });
      }
    } else if (kind === "GAME") {
      if (value) current.gameId = value;
    } else if (kind === "PLY_RANGE") {
      // Format: "10-12" (move numbers) or "19-24" (ply indices)
      const rangeMatch = value.match(/(\d+)\s*-\s*(\d+)/);
      if (rangeMatch) {
        let start = Number(rangeMatch[1]);
        let end = Number(rangeMatch[2]);
        // If numbers look like move numbers (small, < 50), convert to ply indices
        // Move N white = ply (N-1)*2, Move N black = ply (N-1)*2+1
        if (start < 50 && end < 50) {
          start = (start - 1) * 2;
          end = end * 2 - 1;
        }
        current.plyRange = { start, end };
      }
    } else if (kind === "COMPARE") {
      // Format: "e2-e4 | g1-f3" (played | best)
      const compareMatch = value.match(/([a-h][1-8])\s*-\s*([a-h][1-8])\s*\|\s*([a-h][1-8])\s*-\s*([a-h][1-8])/);
      if (compareMatch) {
        current.compare = {
          playedFrom: compareMatch[1],
          playedTo: compareMatch[2],
          bestFrom: compareMatch[3],
          bestTo: compareMatch[4],
        };
      }
    } else if (kind === "EVAL_LINE" && value) {
      // References a live evaluate_line result registered client-side
      // (see live-eval.ts's evaluatedLineStore) — no legality check
      // needed here since the id either resolves to real, already-
      // verified Stockfish step data or it doesn't exist at all;
      // InlineBoard handles a missing/unknown id by just not rendering
      // a navigator, same as any other board with no step data.
      current.evalLineId = value;
    }
  }

  return { blocks, warnings, blockPositions };
};

const isValidFen = (fen: string): boolean => {
  try {
    new Chess(fen);
    return true;
  } catch {
    return false;
  }
};

const isValidSquare = (square: string): boolean => SQUARE_PATTERN.test(square);

const isValidColor = (color: string): boolean => SAFE_COLOR_PATTERN.test(color);

/**
 * Parses "e2-e4" or "e2-e4 red" into a BoardArrow, or null if malformed
 * OR if the move isn't legal from the given FEN. Legality is checked via
 * chess.js rather than trusting the model — see this file's top comment
 * for why (a queen-to-e5 arrow was drawn from f3, which isn't a square
 * the queen can reach; only the bishop on d3 could actually play Be5).
 */
const parseArrow = (candidate: string, fen: string): BoardArrow | null => {
  const parts = candidate.split(/\s+/);
  const [squares, color] = parts;
  const [from, to] = (squares ?? "").split("-");

  if (!from || !to || !isValidSquare(from) || !isValidSquare(to)) return null;
  if (!isValidArrow(fen, from, to)) return null;

  if (color && !isValidColor(color)) return { from, to };
  return color ? { from, to, color } : { from, to };
};

/** Parses "e4" or "e4 red" into a BoardHighlight, or null if malformed. */
const parseHighlight = (candidate: string): BoardHighlight | null => {
  const parts = candidate.split(/\s+/);
  const [square, color] = parts;

  if (!square || !isValidSquare(square)) return null;
  if (color && !isValidColor(color)) return { square };

  return color ? { square, color } : { square };
};

const isValidArrow = (fen: string, from: string, to: string): boolean => {
  try {
    // Must have a piece on the from square
    const piece = new Chess(fen).get(from as Square);
    if (!piece) return false;

    // Geometric check — piece type must be capable of reaching target
    return isPieceGeometricallyCapable(ROLE_NAMES[piece.type], from, to);
  } catch {
    return false;
  }
};

/** Checks if a piece ROLE can geometrically reach from→to regardless of board state. */
const isPieceGeometricallyCapable = (role: string, from: string, to: string): boolean => {
  const df = to.charCodeAt(0) - from.charCodeAt(0);
  const dr = Number.parseInt(to[1]) - Number.parseInt(from[1]);
  const adf = Math.abs(df);
  const adr = Math.abs(dr);

  switch (role) {
    case "knight":
      return (adf === 1 && adr === 2) || (adf === 2 && adr === 1);
    case "bishop":
      return adf === adr && adf > 0;
    case "rook":
      return (adf === 0 || adr === 0) && adf + adr > 0;
    case "queen":
      return (adf === adr && adf > 0) || ((adf === 0 || adr === 0) && adf + adr > 0);
    case "king":
      return adf <= 2 && adr <= 1 && adf + adr > 0;
    case "pawn":
      return adf <= 1 && adr !== 0 && Math.abs(adr) <= 2;
    default:
      return false;
  }
};
